import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Company, CompanyFormData } from '../models/Company';

export class CompanyService {
  private static instance: CompanyService;
  private companiesCollection = collection(db, 'companies');

  private constructor() {}

  static getInstance(): CompanyService {
    if (!CompanyService.instance) {
      CompanyService.instance = new CompanyService();
    }
    return CompanyService.instance;
  }

  async createCompany(data: CompanyFormData): Promise<Company> {
    const companyRef = doc(this.companiesCollection);
    const now = new Date();

    const company: Company = {
      id: companyRef.id,
      name: data.name,
      email: data.email,
      phone: data.phone,
      address: data.address,
      createdAt: now,
      updatedAt: now,
      subscription: {
        plan: data.plan,
        storageLimit: this.getStorageLimit(data.plan),
        userLimit: this.getUserLimit(data.plan),
      },
      settings: {
        folderStructure: 'user',
        namingConvention: '{name}_{date}_{vid}',
        retentionDays: 365,
      },
    };

    await setDoc(companyRef, {
      ...company,
      createdAt: Timestamp.fromDate(company.createdAt),
      updatedAt: Timestamp.fromDate(company.updatedAt),
    });

    return company;
  }

  async getCompany(companyId: string): Promise<Company | null> {
    const companyRef = doc(this.companiesCollection, companyId);
    const companySnap = await getDoc(companyRef);

    if (!companySnap.exists()) {
      return null;
    }

    const data = companySnap.data();

    const company = {
      id: companySnap.id,
      name: data.name,
      email: data.email,
      phone: data.phone,
      address: data.address,
      subscription: data.subscription || {
        plan: 'premium',
        storageLimit: 1000,
        userLimit: 100,
      },
      settings: data.settings || {
        folderStructure: 'user',
        namingConvention: '{name}_{date}_{vid}',
        retentionDays: 365,
      },
      status: data.status,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    } as Company;

    return company;
  }

  async updateCompany(
    companyId: string,
    updates: Partial<Company>
  ): Promise<void> {
    const companyRef = doc(this.companiesCollection, companyId);
    await updateDoc(companyRef, {
      ...updates,
      updatedAt: Timestamp.fromDate(new Date()),
    });
  }

  async updateSettings(
    companyId: string,
    settings: Partial<Company['settings']>
  ): Promise<void> {
    const company = await this.getCompany(companyId);
    if (!company) throw new Error('Company not found');

    await this.updateCompany(companyId, {
      settings: {
        ...company.settings,
        ...settings,
      },
    });
  }

  private getStorageLimit(plan: 'free' | 'basic' | 'premium'): number {
    switch (plan) {
      case 'free':
        return 10; // 10 GB
      case 'basic':
        return 100; // 100 GB
      case 'premium':
        return 1000; // 1 TB
      default:
        return 10;
    }
  }

  private getUserLimit(plan: 'free' | 'basic' | 'premium'): number {
    switch (plan) {
      case 'free':
        return 5;
      case 'basic':
        return 25;
      case 'premium':
        return 100;
      default:
        return 5;
    }
  }
}

// Singleton instance
export const companyService = CompanyService.getInstance();
